import * as React from 'react'
import type { ActivityPrefill } from '@user/features/Activity/components/ActivityForm/hooks/useInitValues'

// Grid surfaces open the create modal by calling into this context rather than
// having an onCreate threaded down through every layer (week/day/month →
// columns → cells), mirroring how tiles reach the detail popover via
// `selectionContext`.

/**
 * What a click on empty grid space carries into the create form.
 *
 * An alias, not a second declaration: the two are cross-assigned when
 * CalendarPage hands this to CreateEventModal, and as separate structural
 * types a field added to one side would silently stop matching rather than
 * failing the build.
 */
export type CreateEventPrefill = ActivityPrefill
type CalendarCreateContextValue = {
  open: (prefill: CreateEventPrefill) => void
}

const CalendarCreateContext =
  React.createContext<CalendarCreateContextValue | null>(null)

export function CalendarCreateProvider({
  open,
  children,
}: {
  open: (prefill: CreateEventPrefill) => void
  children: React.ReactNode
}) {
  const value = React.useMemo(() => ({ open }), [open])
  return (
    <CalendarCreateContext.Provider value={value}>
      {children}
    </CalendarCreateContext.Provider>
  )
}

/**
 * Returns an opener for the create modal, or null when no provider is mounted.
 *
 * Null rather than a no-op so a surface can tell "nobody is hosting create"
 * from "create is available" and skip rendering its click target entirely —
 * an inert button that silently does nothing is worse than no button, and it
 * would still take a tab stop. Grid components stay renderable in isolation
 * (tests, storybook) without a create host.
 */
export function useOpenCreateEvent():
  | ((prefill: CreateEventPrefill) => void)
  | null {
  return React.useContext(CalendarCreateContext)?.open ?? null
}
